import { Box, Chip, Paper, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import { ManualScaleBadge } from '@/components/atoms/ManualScaleBadge'
import { AddSpecialEventButton } from '@/components/molecules/AddSpecialEventButton'
import { ScaleInfoDialog } from '@/components/molecules/ScaleInfoDialog'
import { ScaleTimeline } from '@/components/molecules/ScaleTimeline'
import { ScaleWarnings } from '@/components/molecules/ScaleWarnings'
import { StayDatesPanel } from '@/components/molecules/StayDatesPanel'
import { bombeoPrefill, SPECIAL_EVENT_TYPES } from '@/data'
import { currentPlaceEvent, type EventRow, type ScaleRecord } from '@/data/source'
import { useClock } from '@/lib/clock'

export interface ScaleDetailsProps {
  scale: ScaleRecord
  events: EventRow[]
}

export const ScaleDetails: FC<ScaleDetailsProps> = ({ scale, events }) => {
  const translate = useTranslate()
  const now = useClock().timestamp()
  const currentStay = currentPlaceEvent(events, now)
  const prefill = bombeoPrefill(events.find(event => event.id === currentStay?.id)?.operations ?? [])

  return (
    <Box p={2} display="flex" flexDirection="column" gap={3}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" data-testid="scale-header">
        <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
          {scale.scaleId}
        </Typography>
        {scale.vessel !== null && (
          <Typography variant="h6" component="span" color="text.secondary">
            {scale.vessel}
          </Typography>
        )}
        {scale.isManual && <ManualScaleBadge seenInSource={scale.seenInSource} />}
        {}
        <Chip size="small" label={translate(`enums.ScaleState.${scale.state}`)} data-testid="scale-state" />
        <Box display="flex" gap={1} ml="auto">
          <ScaleInfoDialog scale={scale} events={events} />
          {!scale.closed &&
            SPECIAL_EVENT_TYPES.map(type => (
              <AddSpecialEventButton key={type} scale={scale} type={type} prefill={type === 'BOMBEO' ? prefill : undefined} />
            ))}
        </Box>
      </Box>

      {}

      {events.length === 0 ? (
        <Paper variant="outlined">
          <Typography p={2} color="text.secondary">
            {translate('pages.scales.no_events')}
          </Typography>
        </Paper>
      ) : (
        <>
          <ScaleTimeline events={events} now={now} />
          <StayDatesPanel events={events} now={now} />
          <ScaleWarnings events={events} now={now} />
        </>
      )}
    </Box>
  )
}
